/* A2 — Workflow Interface : productise la boucle Wave A (Decision → Approve/Reject →
   Action → Evidence → Outcome). Branchée EXCLUSIVEMENT sur /workspace/workflows
   (moteur vertical_loop backend) — aucun second moteur. Action ≠ Outcome. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { GitBranch, Loader2, CheckCircle2, XCircle, Clock, FileCheck2, Eye } from "lucide-react";

const STATUS_STYLE = {
  pending_approval: "text-[#EF9F27] border-[#EF9F27]/40",
  approved: "text-[#378ADD] border-[#378ADD]/40",
  rejected: "text-[#E24B4A] border-[#E24B4A]/40",
  action_completed: "text-[#7F77DD] border-[#7F77DD]/40",
  outcome_observed: "text-[#1D9E75] border-[#1D9E75]/40",
};
const STATUS_LABEL = {
  pending_approval: "En attente d'approbation",
  approved: "Approuvé — action en cours",
  rejected: "Rejeté",
  action_completed: "Action complétée — outcome à observer",
  outcome_observed: "Outcome observé",
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" }) : "—");

function StepDot({ status }) {
  if (status === "done") return <CheckCircle2 className="w-3.5 h-3.5 text-[#1D9E75]" />;
  if (status === "rejected") return <XCircle className="w-3.5 h-3.5 text-[#E24B4A]" />;
  return <Clock className="w-3.5 h-3.5 text-zinc-500" />;
}

function WorkflowCard({ wf, onAction }) {
  const [open, setOpen] = useState(false);
  const [evidence, setEvidence] = useState("");
  const [observed, setObserved] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(null);
  const decision = wf.provenance?.decision;

  const act = async (verb, body) => {
    setBusy(verb);
    try {
      await api.post(`/workspace/workflows/${wf.id}/${verb}`, body || {});
      notify.success(verb === "approve" ? "Workflow approuvé" : verb === "reject" ? "Workflow rejeté"
        : verb === "complete-action" ? "Action complétée — l'outcome reste à observer" : "Outcome enregistré (LEARN)");
      onAction();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(null);
  };

  return (
    <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid={`workflow-card-${wf.id}`}>
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <GitBranch className="w-4 h-4 text-[#7F77DD] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-medium truncate">{wf.title}</div>
          <div className="text-[11px] text-zinc-500 truncate">
            Action : {wf.action} · Owner : <span className="text-zinc-300">{wf.owner_email}</span>
            {wf.deadline && <> · Deadline : <span className="text-zinc-300">{wf.deadline}</span></>}
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[wf.status] || "text-zinc-400 border-white/15"}`}
          data-testid={`workflow-status-${wf.id}`}>
          {STATUS_LABEL[wf.status] || wf.status}
        </span>
      </div>

      {/* Timeline : Approbation → Action → Outcome (trois états DISTINCTS) */}
      <div className="mt-2 flex items-center gap-4 text-[10px] text-zinc-400">
        {(wf.steps || []).map((s, i) => (
          <span key={i} className="flex items-center gap-1">
            <StepDot status={s.status} />
            {s.kind === "approval" ? "Approbation" : s.kind === "action" ? "Action" : "Outcome"}
            {s.at && <span className="text-zinc-600">· {fmtDate(s.at)}</span>}
            {s.by && <span className="text-zinc-600">· {s.by}</span>}
          </span>
        ))}
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-3 text-[11px]" data-testid={`workflow-detail-${wf.id}`}>
          {/* Inspection : rationale + provenance de la décision liée */}
          {decision ? (
            <div className="p-2 rounded border border-[#7F77DD]/25 bg-[#7F77DD]/5">
              <div className="text-zinc-300 flex items-center gap-1"><Eye className="w-3 h-3" />
                Décision liée : <b className="text-white">{decision.titre || decision.id}</b>
                <span className="text-zinc-500">({wf.decision_id})</span></div>
              <div className="text-zinc-500 mt-0.5">Provenance : {wf.provenance?.source}</div>
            </div>
          ) : (
            <div className="text-zinc-500">Workflow autonome (aucune décision liée) · Provenance : {wf.provenance?.source}</div>
          )}

          {wf.steps?.[1]?.evidence && (
            <div className="text-zinc-300"><FileCheck2 className="w-3 h-3 inline mr-1" />Evidence : « {wf.steps[1].evidence} »</div>
          )}
          {wf.steps?.[2]?.observed && (
            <div className="text-[#1D9E75]">Outcome observé : « {wf.steps[2].observed} »</div>
          )}
          {wf.steps?.[0]?.reason && (
            <div className="text-[#E24B4A]">Motif de rejet : « {wf.steps[0].reason} »</div>
          )}

          {/* Actions humaines selon l'état (human-in-the-loop) */}
          {wf.status === "pending_approval" && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => act("approve")} disabled={!!busy} data-testid={`workflow-approve-${wf.id}`}
                className="px-3 py-1.5 rounded bg-[#1D9E75]/20 border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/30 text-xs">
                {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Approuver"}
              </button>
              <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motif de rejet (optionnel)"
                data-testid={`workflow-reject-reason-${wf.id}`}
                className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white w-52" />
              <button onClick={() => act("reject", { reason: rejectReason })} disabled={!!busy} data-testid={`workflow-reject-${wf.id}`}
                className="px-3 py-1.5 rounded bg-[#E24B4A]/20 border border-[#E24B4A]/40 text-[#E24B4A] hover:bg-[#E24B4A]/30 text-xs">
                Rejeter
              </button>
            </div>
          )}
          {wf.status === "approved" && (
            <div className="flex flex-wrap items-center gap-2">
              <input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Evidence de l'action réalisée"
                data-testid={`workflow-evidence-input-${wf.id}`}
                className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white w-64" />
              <button onClick={() => act("complete-action", { evidence })} disabled={!!busy || !evidence.trim()}
                data-testid={`workflow-complete-action-${wf.id}`}
                className="px-3 py-1.5 rounded bg-[#7F77DD]/20 border border-[#7F77DD]/40 text-[#7F77DD] hover:bg-[#7F77DD]/30 text-xs disabled:opacity-40">
                Compléter l'action
              </button>
            </div>
          )}
          {wf.status === "action_completed" && (
            <div className="space-y-1">
              <div className="text-[10px] text-[#EF9F27]">Doctrine : ACTION COMPLÉTÉE ≠ OUTCOME OBSERVÉ — l'effet réel doit être constaté séparément.</div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={observed} onChange={(e) => setObserved(e.target.value)} placeholder="Outcome réellement observé"
                  data-testid={`workflow-outcome-input-${wf.id}`}
                  className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white w-64" />
                <button onClick={() => act("record-outcome", { observed })} disabled={!!busy || !observed.trim()}
                  data-testid={`workflow-record-outcome-${wf.id}`}
                  className="px-3 py-1.5 rounded bg-[#1D9E75]/20 border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/30 text-xs disabled:opacity-40">
                  Enregistrer l'outcome
                </button>
              </div>
            </div>
          )}

          {/* Audit trail */}
          {(wf.audit || []).length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Audit trail</div>
              {wf.audit.map((a, i) => (
                <div key={i} className="text-[10px] text-zinc-500">
                  {fmtDate(a.at)} · <span className="text-zinc-300">{a.event}</span>
                  {a.by && <> · {a.by}</>}{a.reason && <> · « {a.reason} »</>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Workflows() {
  const [workflows, setWorkflows] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", action: "", owner_email: "", deadline: "", decision_id: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/workspace/workflows").then((r) => setWorkflows(r.data)).catch((e) => { notify.error(fmtError(e)); setWorkflows([]); });
  };
  useEffect(() => {
    load();
    api.get("/workspace/decisions").then((r) => setDecisions(Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => {});
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/workspace/workflows", {
        title: form.title, action: form.action, owner_email: form.owner_email,
        deadline: form.deadline || null, decision_id: form.decision_id || null,
      });
      notify.success("Workflow créé — en attente d'approbation humaine");
      setForm({ title: "", action: "", owner_email: "", deadline: "", decision_id: "" });
      setShowCreate(false);
      load();
    } catch (e) { notify.error(fmtError(e)); }
    setCreating(false);
  };

  return (
    <div className="p-6 max-w-4xl space-y-4" data-testid="workflows-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#7F77DD]" /> Workflow
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Decision → Approbation humaine → Action → Evidence → Outcome. Chaque étape est distincte et auditée.
          </p>
        </div>
        <button onClick={() => setShowCreate((v) => !v)} data-testid="workflow-create-toggle"
          className="px-3 py-1.5 rounded bg-[#7F77DD]/20 border border-[#7F77DD]/40 text-[#7F77DD] hover:bg-[#7F77DD]/30 text-xs">
          {showCreate ? "Fermer" : "Nouveau workflow"}
        </button>
      </div>

      {showCreate && (
        <div className="rounded border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid="workflow-create-form">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre"
              data-testid="workflow-form-title" className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" />
            <input value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="Owner (email)"
              data-testid="workflow-form-owner" className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" />
            <input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} placeholder="Action à réaliser"
              data-testid="workflow-form-action" className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white col-span-2" />
            <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              data-testid="workflow-form-deadline" className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white" />
            <select value={form.decision_id} onChange={(e) => setForm({ ...form, decision_id: e.target.value })}
              data-testid="workflow-form-decision" className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-white">
              <option value="">— Sans décision liée —</option>
              {decisions.map((d) => <option key={d.id} value={d.id}>{d.titre || d.title || d.id}</option>)}
            </select>
          </div>
          <button onClick={create} disabled={creating || !form.title.trim() || !form.action.trim() || !form.owner_email.trim()}
            data-testid="workflow-form-submit"
            className="px-3 py-1.5 rounded bg-[#1D9E75]/20 border border-[#1D9E75]/40 text-[#1D9E75] text-xs disabled:opacity-40">
            {creating ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Créer"}
          </button>
        </div>
      )}

      {workflows === null ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : workflows.length === 0 ? (
        <div className="text-zinc-500 text-sm rounded border border-dashed border-white/10 p-6 text-center" data-testid="workflows-empty">
          Aucun workflow. Créez-en un depuis une décision pour fermer la boucle Decision → Action → Outcome.
        </div>
      ) : (
        <div className="space-y-2" data-testid="workflows-list">
          {workflows.map((wf) => <WorkflowCard key={wf.id} wf={wf} onAction={load} />)}
        </div>
      )}
    </div>
  );
}
